import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  LinearProgress,
  Button,
  Paper,
  Stack,
  alpha,
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Inbox as InboxIcon,
  EditNote as ComposeIcon,
  Schedule as PendingIcon,
  CheckCircle as SentIcon,
  ErrorOutline as FailedIcon,
  School as SchoolIcon,
  DoneAll as ReadIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import whatsappApiService from "../services/whatsappApiService";
import { usePageHeader } from "../contexts/PageHeaderContext";

const WA_GREEN = "#25D366";
const WA_DARK = "#128C7E";

const QUEUE_TABS = [
  { key: "pending", label: "Pending", icon: PendingIcon, color: "#ed6c02" },
  { key: "sent", label: "Sent", icon: SentIcon, color: "#2e7d32" },
  { key: "failed", label: "Failed", icon: FailedIcon, color: "#d32f2f" },
];

const STATUS_CHIP_STYLES = {
  pending: { bg: alpha("#ed6c02", 0.12), color: "#e65100", border: alpha("#ed6c02", 0.35) },
  processing: { bg: alpha("#0288d1", 0.12), color: "#0277bd", border: alpha("#0288d1", 0.35) },
  sent: { bg: alpha(WA_GREEN, 0.14), color: WA_DARK, border: alpha(WA_GREEN, 0.4) },
  delivered: { bg: alpha(WA_GREEN, 0.14), color: WA_DARK, border: alpha(WA_GREEN, 0.4) },
  read: { bg: alpha("#2e7d32", 0.12), color: "#2e7d32", border: alpha("#2e7d32", 0.35) },
  failed: { bg: alpha("#d32f2f", 0.1), color: "#c62828", border: alpha("#d32f2f", 0.35) },
};

const TABLE_HEAD_SX = {
  fontWeight: 700,
  fontSize: "0.6875rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "text.secondary",
  bgcolor: alpha(WA_GREEN, 0.06),
  borderBottom: `2px solid ${alpha(WA_GREEN, 0.2)}`,
  py: 1.1,
  px: 2,
  whiteSpace: "nowrap",
};

const TABLE_CELL_SX = {
  py: 1.15,
  px: 2,
  fontSize: "0.8125rem",
  borderColor: alpha(WA_GREEN, 0.08),
  verticalAlign: "middle",
};

const NAV_ACTION_TONES = {
  compose: {
    main: WA_DARK,
    bg: alpha(WA_GREEN, 0.12),
    border: alpha(WA_GREEN, 0.32),
    hoverBg: alpha(WA_GREEN, 0.2),
  },
  inbox: {
    main: "#1976d2",
    bg: alpha("#1976d2", 0.1),
    border: alpha("#1976d2", 0.28),
    hoverBg: alpha("#1976d2", 0.16),
  },
  refresh: {
    main: "#6a1b9a",
    bg: alpha("#6a1b9a", 0.1),
    border: alpha("#6a1b9a", 0.28),
    hoverBg: alpha("#6a1b9a", 0.16),
  },
};

function getStatusChipStyle(status) {
  return STATUS_CHIP_STYLES[status?.toLowerCase()] ?? {
    bg: alpha("#64748b", 0.1),
    color: "#475569",
    border: alpha("#64748b", 0.25),
  };
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const NavActionButton = memo(function NavActionButton({ icon, label, onClick, disabled, tone }) {
  const palette = NAV_ACTION_TONES[tone];

  return (
    <Button
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        textTransform: "none",
        borderRadius: 2,
        px: 1.5,
        py: 0.65,
        minHeight: 36,
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: palette.main,
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow: `0 1px 3px ${alpha(palette.main, 0.12)}`,
        gap: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: palette.hoverBg,
          borderColor: palette.main,
          boxShadow: `0 4px 14px ${alpha(palette.main, 0.22)}`,
          transform: "translateY(-1px)",
        },
        "&.Mui-disabled": {
          color: alpha(palette.main, 0.45),
          bgcolor: alpha(palette.main, 0.05),
          borderColor: alpha(palette.main, 0.15),
        },
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(palette.main, 0.14),
          color: palette.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      {label}
    </Button>
  );
});

const QueueStatCard = memo(function QueueStatCard({ tab, count, active, onClick }) {
  const Icon = tab.icon;

  return (
    <Paper
      component="button"
      type="button"
      onClick={onClick}
      elevation={0}
      sx={{
        p: 1.25,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        border: 2,
        borderColor: active ? tab.color : alpha(tab.color, 0.22),
        borderRadius: 2,
        bgcolor: active ? alpha(tab.color, 0.1) : "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: tab.color,
          bgcolor: alpha(tab.color, 0.08),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(tab.color, 0.14),
            color: tab.color,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {tab.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {active ? "Showing" : "View list"}
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: tab.color, lineHeight: 1 }}>
          {count}
        </Typography>
      </Box>
    </Paper>
  );
});

const StatusChip = memo(function StatusChip({ status }) {
  const style = getStatusChipStyle(status);
  const label = status || "unknown";

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.6875rem",
        height: 24,
        textTransform: "capitalize",
        bgcolor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        "& .MuiChip-label": { px: 1.1 },
      }}
    />
  );
});

const SchoolCell = memo(function SchoolCell({ schoolCode }) {
  if (!schoolCode) {
    return (
      <Chip
        label="Direct"
        size="small"
        variant="outlined"
        sx={{
          height: 24,
          fontSize: "0.6875rem",
          fontWeight: 600,
          borderColor: alpha("#64748b", 0.35),
          color: "text.secondary",
        }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(WA_DARK, 0.1),
          color: WA_DARK,
          flexShrink: 0,
        }}
      >
        <SchoolIcon sx={{ fontSize: 15 }} />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>
        {schoolCode}
      </Typography>
    </Box>
  );
});

const ContactCell = memo(function ContactCell({ contactNumber }) {
  if (!contactNumber) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.35,
        borderRadius: 1.5,
        bgcolor: alpha(WA_GREEN, 0.08),
        border: `1px solid ${alpha(WA_GREEN, 0.2)}`,
      }}
    >
      <WhatsAppIcon sx={{ fontSize: 14, color: WA_GREEN }} />
      <Typography
        variant="body2"
        sx={{ fontFamily: "ui-monospace, monospace", fontSize: "0.75rem", fontWeight: 600 }}
      >
        {contactNumber}
      </Typography>
    </Box>
  );
});

const MessageCell = memo(function MessageCell({ message }) {
  if (!message) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Tooltip title={message} placement="top-start">
      <Box
        sx={{
          maxWidth: 280,
          px: 1.25,
          py: 0.65,
          borderRadius: 1.5,
          bgcolor: alpha("#64748b", 0.06),
          border: `1px solid ${alpha("#64748b", 0.12)}`,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {message}
        </Typography>
      </Box>
    </Tooltip>
  );
});

const DateCell = memo(function DateCell({ value, icon: Icon = TimeIcon }) {
  const formatted = formatDateTime(value);
  if (formatted === "—") {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Icon sx={{ fontSize: 14, color: "text.disabled" }} />
      <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary" }}>
        {formatted}
      </Typography>
    </Box>
  );
});

const WhatsAppQueue = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, sent: 0, failed: 0, replies: 0 });
  const [loading, setLoading] = useState(false);

  const activeGroup = QUEUE_TABS[activeTab]?.key ?? "pending";

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await whatsappApiService.getMessageQueue(activeGroup, 200, 0);
      if (result.success) {
        setItems(result.data?.items ?? []);
        setCounts((prev) => ({
          ...prev,
          ...(result.data?.counts ?? {}),
        }));
      } else {
        toast.error(result.error || "Failed to load queue");
        setItems([]);
      }
    } catch (error) {
      toast.error("Failed to load data: " + error.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeGroup]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const tabCount = (key) => counts[key] ?? 0;
  const tableColSpan = activeGroup === "sent" ? 7 : 6;
  const queueTotal = tabCount("pending") + tabCount("sent") + tabCount("failed");
  const replyCount = tabCount("replies");

  usePageHeader({
    title: "WhatsApp Message Queue",
    breadcrumbs: [
      {
        label: "WhatsApp Message Queue",
        icon: <SyncIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
      },
    ],
    backTo: "/whatsapp",
  });

  const inboxLabel = useMemo(
    () => (replyCount > 0 ? `Inbox (${replyCount})` : "Inbox"),
    [replyCount]
  );

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Card sx={{ bgcolor: "#fff" }}>
          <Box
            className="page-actions-bar"
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2.5,
              py: 1.25,
              minHeight: 52,
              backgroundColor: "var(--color-primary-surface-muted)",
              borderBottom: "1px solid var(--border-color-hover)",
              borderRadius: "var(--radius-md) var(--radius-md) 0 0",
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Message Queue
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {queueTotal} total · {tabCount(activeGroup)} {activeGroup}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <NavActionButton
                tone="compose"
                label="Compose"
                icon={<ComposeIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/whatsapp")}
              />
              <NavActionButton
                tone="inbox"
                label={inboxLabel}
                icon={<InboxIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/whatsapp/inbox")}
              />
              <NavActionButton
                tone="refresh"
                label="Refresh"
                icon={<RefreshIcon sx={{ fontSize: 16 }} />}
                onClick={loadData}
                disabled={loading}
              />
            </Stack>
          </Box>

          <CardContent sx={{ p: 2, bgcolor: "#fff" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 1.5,
                mb: 2,
              }}
            >
              {QUEUE_TABS.map((tab, index) => (
                <QueueStatCard
                  key={tab.key}
                  tab={tab}
                  count={tabCount(tab.key)}
                  active={activeTab === index}
                  onClick={() => setActiveTab(index)}
                />
              ))}
            </Box>

            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: alpha(WA_GREEN, 0.2),
                borderRadius: 2.5,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: alpha(WA_GREEN, 0.06),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => setActiveTab(value)}
                  sx={{
                    minHeight: 36,
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      minHeight: 36,
                      py: 0.25,
                      fontSize: "0.8125rem",
                    },
                  }}
                >
                  {QUEUE_TABS.map((tab) => (
                    <Tab
                      key={tab.key}
                      label={`${tab.label} (${tabCount(tab.key)})`}
                    />
                  ))}
                </Tabs>
                <IconButton size="small" onClick={loadData} disabled={loading} aria-label="Refresh list">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>

              {loading && <LinearProgress color="success" />}

              <TableContainer sx={{ maxHeight: "calc(100vh - 380px)" }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    "& .MuiTableCell-head": TABLE_HEAD_SX,
                    "& .MuiTableCell-body": TABLE_CELL_SX,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>School</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Message</TableCell>
                      <TableCell>Status</TableCell>
                      {activeGroup === "pending" && <TableCell>Scheduled</TableCell>}
                      {activeGroup === "sent" && (
                        <>
                          <TableCell>Sent At</TableCell>
                          <TableCell>Delivery</TableCell>
                        </>
                      )}
                      {activeGroup === "failed" && <TableCell>Error</TableCell>}
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={tableColSpan} align="center" sx={{ borderBottom: 0 }}>
                          <Box sx={{ py: 5 }}>
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                mx: "auto",
                                mb: 1.5,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(WA_GREEN, 0.1),
                              }}
                            >
                              <WhatsAppIcon sx={{ fontSize: 32, color: alpha(WA_GREEN, 0.55) }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                              No {activeGroup} messages
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: "auto" }}>
                              {activeGroup === "pending"
                                ? "New sends from Compose will appear here until processed."
                                : `No ${activeGroup} items in the queue right now.`}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((row, index) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            bgcolor: index % 2 === 1 ? alpha(WA_GREEN, 0.02) : "transparent",
                            transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                            "&:last-child td": { borderBottom: 0 },
                            "&:hover": {
                              bgcolor: alpha(WA_GREEN, 0.06),
                              boxShadow: `inset 3px 0 0 ${WA_GREEN}`,
                            },
                          }}
                        >
                          <TableCell>
                            <SchoolCell schoolCode={row.schoolCode} />
                          </TableCell>
                          <TableCell>
                            <ContactCell contactNumber={row.contactNumber} />
                          </TableCell>
                          <TableCell>
                            <MessageCell message={row.message} />
                          </TableCell>
                          <TableCell>
                            <StatusChip status={row.status} />
                          </TableCell>
                          {activeGroup === "pending" && (
                            <TableCell>
                              <DateCell value={row.scheduledAt} />
                            </TableCell>
                          )}
                          {activeGroup === "sent" && (
                            <>
                              <TableCell>
                                <DateCell value={row.sentAt} icon={SentIcon} />
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  {row.deliveredAt ? (
                                    <DateCell value={row.deliveredAt} icon={SentIcon} />
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      Not delivered
                                    </Typography>
                                  )}
                                  {row.readAt && (
                                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                      <ReadIcon sx={{ fontSize: 14, color: WA_DARK }} />
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: WA_DARK }}>
                                        Read {formatDateTime(row.readAt)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>
                              </TableCell>
                            </>
                          )}
                          {activeGroup === "failed" && (
                            <TableCell>
                              {row.errorMessage ? (
                                <Tooltip title={row.errorMessage} placement="top-start">
                                  <Box
                                    sx={{
                                      maxWidth: 220,
                                      px: 1.1,
                                      py: 0.5,
                                      borderRadius: 1.5,
                                      bgcolor: alpha("#d32f2f", 0.08),
                                      border: `1px solid ${alpha("#d32f2f", 0.2)}`,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#c62828",
                                        fontSize: "0.8125rem",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {row.errorMessage}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <DateCell value={row.createdAt} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {!loading && items.length > 0 && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderTop: 1,
                    borderColor: alpha(WA_GREEN, 0.12),
                    bgcolor: alpha(WA_GREEN, 0.03),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Showing {items.length} {activeGroup} message{items.length === 1 ? "" : "s"}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: WA_DARK }}>
                    {QUEUE_TABS[activeTab]?.label} queue
                  </Typography>
                </Box>
              )}
            </Paper>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default WhatsAppQueue;
